import React, { useEffect, useMemo, useState } from 'react';
import {
    Badge,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    Grid,
    Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Check } from '@mui/icons-material';
import cn from 'classnames';
import partial from 'lodash/partial';
import startCase from 'lodash/startCase';
import ChipList from '@dyl/components/ChipList';
import useStylesSubset from '@dyl/hooks/useStylesSubset';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import dictionary from '@dyl/utils/dictionary';
import { map, union } from 'lodash';
import extractPropertiesDeep from '@dyl/utils/extractPropertiesDeep';
import ImagePicker from '@dyl/components/ImagePicker';
import checkTargets from "@dyl/apiClients/backendApi/checkTargets";
import AddFromTemplateModal from "./AddFromTemplateModal";
import CopyFromExcelModal
    from "@dyl/task-types/sharedLogic/SpeakingGenericEditor/AutomarkerTargetsPopupButton/CopyFromExcelModal";
import SpecialSymbolStrip from "@dyl/utils/specialSymbolStrip";

const useStyles = makeStyles(theme => ({
    status_entered: {
        '& $badgeBadge': {
            backgroundColor: theme.palette.success.main,
        },
    },

    badgeRoot: {
        alignSelf: 'flex-start',
    },
    badgeBadge: {
        padding: 0,
    },

    section: {
        marginBottom: theme.spacing(3),
    },

    imagesContainer: {
        marginBottom: theme.spacing(3),
    },
}));

/**
 * @type {import('@dyl/utils/dictionary').Dictionary<string, {
 *  helperText: string
 * }}
 */
const sections = dictionary([
    ['primary_targets', {
        helperText: 'Targets for the correct answer(s)',
    }],
    ['secondary_targets', {
        helperText: 'Targets for additional words that may be used in an answer',
    }]
])

export default function AutomarkerTargetsPopupButton({ index }) {
    const classes = useStyles();
    const badgeClasses = useStylesSubset(classes, 'badge');
    const useField = partial(useTaskField, 'items', index);

    const [item] = useTaskField('items', index);
    const [primaryTargets, setPrimaryTargets] = useField('primary_targets');
    const [secondaryTargets, setSecondaryTargets] = useField('secondary_targets');

    const isDone = primaryTargets?.length > 0 || secondaryTargets?.length > 0;

    const [isOpen, setOpen] = useState(false);

    const images = useMemo(() => extractPropertiesDeep(item, 'image_id'), [item]);

    // Hold onto the onOpen state of the targets in order to be able to undo the changes.
    const [initialState, setInitialState] = useState(null);
    useEffect(() => {
        if (!isOpen) return;
        setInitialState({
            primaryTargets,
            secondaryTargets
        });
    }, [isOpen]);

    // Set already saved data as valid initially.
    const [targetDictionary, setTargetDictionary] = useState(
        Object.fromEntries((primaryTargets ?? []).concat(secondaryTargets ?? []).map(x => [x, true]))
    );

    const onSave = async() => {
        const data = await checkTargets((primaryTargets ?? []).concat(secondaryTargets ?? []));
        const results = Object.fromEntries(data.map(x => [x.target, x.valid]));
        setTargetDictionary(results);
        if (!data.some(r => !r.valid)) { // If no errors.
            setOpen(false);
        } else {
            // Show some error
        }
    };

    const onClose = () => {
        setPrimaryTargets(initialState.primaryTargets);
        setSecondaryTargets(initialState.secondaryTargets);
        setOpen(false);
    };

    const onAdd = (template) => {
        if (!!template?.primary_targets) {
            setPrimaryTargets(union(primaryTargets ?? [], template.primary_targets ?? []));
        }
        if (!!template?.secondary_targets) {
            setSecondaryTargets(union(secondaryTargets ?? [], template.secondary_targets ?? []));
        }
    };

    return (
        <>
            <Badge
                className={cn(isDone && classes.status_entered)}
                classes={badgeClasses}
                badgeContent={isDone ? <Check fontSize='inherit' /> : '!'}
                color='error'
            >
                <Button variant='contained' onClick={() => setOpen(true)}>
                    Edit Automarker Targets
                </Button>
            </Badge>

            <Dialog
                open={isOpen}
                onClose={onClose}
                fullWidth
                maxWidth='md'
            >
                <DialogTitle>
                    Item {index + 1} Automarker Targets
                </DialogTitle>

                <DialogContent>
                    <Grid>
                        <Typography variant={'body1'}>Images in Item</Typography>
                    </Grid>
                    <Grid container spacing={3} className={classes.imagesContainer}>
                        {images.map((imageId, index) => (
                            <Grid item key={index} xs={6} sm={4} md={3}>
                                <ImagePicker
                                    value={imageId}
                                    disabled
                                />
                            </Grid>
                        ))}
                    </Grid>

                    <Grid>
                        <Typography style={{ marginTop: '16px', marginBottom: '16px' }} variant={'h6'}>Targets</Typography>
                        <AddFromTemplateModal onAdd={onAdd} />
                        <CopyFromExcelModal onAdd={onAdd} />
                    </Grid>

                    {map(sections, (value, key) => (
                        <TargetsSection
                            key={key}
                            className={classes.section}
                            itemIndex={index}
                            property={key}
                            targetDictionary={targetDictionary}
                        />
                    ))}
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose}>
                        Close
                    </Button>
                    <Button variant='contained' color='primary' onClick={onSave}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    );
}

/**
 * @param {Object} props
 * @param {number} props.itemIndex
 * @param {string} props.property
 */
function TargetsSection({ itemIndex, property, targetDictionary, disabled, ...other }) {
    const [targets, setTargets, targetsError] = useTaskField('items', itemIndex, property);

    const { helperText } = sections[property];

    return (
        <div {...other}>
            <Grid style={{ marginTop: '8px' }}>
                <Typography variant='h6'>
                    {startCase(property)}
                </Typography>
            </Grid>

            <FormHelperText error={!!targetsError} style={{ marginBottom: '8px' }}>
                {/* Display a space if empty, so layout is consistent */}
                {targetsError || helperText || ' '}
            </FormHelperText>

            <ChipList
                value={targets}
                onChange={setTargets}
                transformFunction={SpecialSymbolStrip}
                targetDictionary={targetDictionary}
            />
        </div>
    );
}